import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import { Save } from "@mui/icons-material";
import { appVersionService, AppVersion, UpdateAppVersionDto, AppType } from "../../apis/appVersions";

const AppVersionList: React.FC = () => {
  const [riderVersion, setRiderVersion] = useState<AppVersion | null>(null);
  const [driverVersion, setDriverVersion] = useState<AppVersion | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [activeTab, setActiveTab] = useState<AppType>("rider");
  const [riderFormData, setRiderFormData] = useState<UpdateAppVersionDto>({
    app_type: "rider",
    play_store_version: "",
    app_store_version: "",
    huawei_store_version: "",
  });
  const [driverFormData, setDriverFormData] = useState<UpdateAppVersionDto>({
    app_type: "driver",
    play_store_version: "",
    app_store_version: "",
    huawei_store_version: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" });

  useEffect(() => {
    fetchAppVersions();
  }, []);

  const fetchAppVersions = async () => {
    setFetching(true);
    try {
      // Fetch both rider and driver versions
      const [riderResponse, driverResponse] = await Promise.all([
        appVersionService.getLatestAppVersion("rider"),
        appVersionService.getLatestAppVersion("driver"),
      ]);

      // Handle rider version
      if (riderResponse.status === "success" || riderResponse.status === true) {
        const versionData = riderResponse.data;
        if (versionData && versionData.play_store_version) {
          setRiderVersion(versionData as AppVersion);
          setRiderFormData({
            app_type: "rider",
            play_store_version: versionData.play_store_version || "",
            app_store_version: versionData.app_store_version || "",
            huawei_store_version: versionData.huawei_store_version || "",
          });
        } else {
          setRiderFormData({
            app_type: "rider",
            play_store_version: "",
            app_store_version: "",
            huawei_store_version: "",
          });
        }
      }

      // Handle driver version
      if (driverResponse.status === "success" || driverResponse.status === true) {
        const versionData = driverResponse.data;
        if (versionData && versionData.play_store_version) {
          setDriverVersion(versionData as AppVersion);
          setDriverFormData({
            app_type: "driver",
            play_store_version: versionData.play_store_version || "",
            app_store_version: versionData.app_store_version || "",
            huawei_store_version: versionData.huawei_store_version || "",
          });
        } else {
          setDriverFormData({
            app_type: "driver",
            play_store_version: "",
            app_store_version: "",
            huawei_store_version: "",
          });
        }
      }
    } catch (error: any) {
      console.error("Error fetching app versions:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch app versions",
        severity: "error",
      });
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, appType: AppType) => {
    const { name, value } = e.target;
    if (appType === "rider") {
      setRiderFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else {
      setDriverFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (appType: AppType) => {
    const formData = appType === "rider" ? riderFormData : driverFormData;
    
    // Validation
    if (!formData.play_store_version || !formData.app_store_version || !formData.huawei_store_version) {
      setSnackbar({
        open: true,
        message: `Please fill in all version fields for ${appType} app`,
        severity: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await appVersionService.updateAppVersion(formData);
      setSnackbar({
        open: true,
        message: `${appType.charAt(0).toUpperCase() + appType.slice(1)} app versions updated successfully`,
        severity: "success",
      });
      // Refresh the data
      await fetchAppVersions();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || `Failed to update ${appType} app versions`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const renderVersionForm = (appType: AppType) => {
    const formData = appType === "rider" ? riderFormData : driverFormData;
    const appVersion = appType === "rider" ? riderVersion : driverVersion;
    const appName = appType === "rider" ? "Rider" : "Driver";

    return (
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6">{appName} App Versions</Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit(appType)}
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? "Saving..." : `Save ${appName} Versions`}
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Play Store Version"
                name="play_store_version"
                value={formData.play_store_version}
                onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, appType)}
                fullWidth
                required
                helperText="Version for Google Play Store"
                placeholder="e.g., 1.0.0"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="App Store Version"
                name="app_store_version"
                value={formData.app_store_version}
                onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, appType)}
                fullWidth
                required
                helperText="Version for Apple App Store"
                placeholder="e.g., 1.0.0"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Huawei Store Version"
                name="huawei_store_version"
                value={formData.huawei_store_version}
                onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>, appType)}
                fullWidth
                required
                helperText="Version for Huawei AppGallery"
                placeholder="e.g., 1.0.0"
              />
            </Grid>
          </Grid>

          {appVersion && (
            <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {new Date(appVersion.updated_at).toLocaleString()}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (fetching) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 3 }}>
          App Versions Management
        </Typography>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        App Versions Management
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Rider App" value="rider" />
          <Tab label="Driver App" value="driver" />
        </Tabs>
      </Box>

      {activeTab === "rider" && renderVersionForm("rider")}
      {activeTab === "driver" && renderVersionForm("driver")}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AppVersionList;

